import { Link } from "react-router-dom";
import { Phone, ShieldCheck, CalendarDays, ArrowRight, Star, MapPin, BookOpen } from "lucide-react";
import { ASSETS, CLINIC } from "../lib/api";
import { useServices } from "../hooks/useServices";
import { ServiceImage } from "../components/ServiceImage";
import { MapEmbed } from "../components/MapEmbed";

export default function Home() {
  const SERVICES = useServices();

  return (
    <div className="no-img-drag">
      {/* HERO — Sigla mascotă + Nume cabinet + nume site + CTAs */}
      <section className="relative bg-magical-hero">
        {/* Floating accents */}
        <span aria-hidden className="absolute top-[8%] left-[6%] text-brand-yellow opacity-80 animate-twinkle z-10"><Star className="w-5 h-5" fill="currentColor" /></span>
        <span aria-hidden className="absolute top-[18%] right-[8%] text-brand-yellow opacity-80 animate-twinkle-slow z-10" style={{animationDelay:'.7s'}}><Star className="w-4 h-4" fill="currentColor" /></span>
        <span aria-hidden className="absolute bottom-[12%] left-[8%] text-brand-yellow opacity-70 animate-twinkle z-10" style={{animationDelay:'1.4s'}}><Star className="w-3 h-3" fill="currentColor" /></span>
        <span aria-hidden className="absolute bottom-[20%] right-[10%] text-brand-yellow opacity-80 animate-twinkle-slow z-10" style={{animationDelay:'.4s'}}><Star className="w-4 h-4" fill="currentColor" /></span>

        <div className="relative max-w-6xl mx-auto container-x py-12 sm:py-16 lg:py-20 text-center">
          {/* Portret oficial HERO — Dr. Biriș Cornelia Maria în cabinet, Cluj-Napoca */}
          <div className="mx-auto w-full max-w-[280px] sm:max-w-sm lg:max-w-md rounded-3xl overflow-hidden shadow-2xl bg-brand-cream" data-testid="hero-doctor-portrait">
            <div className="aspect-[3/4] w-full">
              <img
                src={ASSETS.heroPortrait}
                alt="Dr. Cornelia Maria Biriș în cabinetul în care își desfășoară activitatea în Cluj-Napoca."
                className="block w-full h-full object-cover"
                style={{ objectPosition: "center 25%" }}
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
            </div>
          </div>

          {/* Titlu principal — numele cabinetului + numele site-ului explicit */}
          <h1 className="mt-8 sm:mt-10 font-display font-extrabold text-brand-navy text-4xl sm:text-5xl lg:text-6xl tracking-tight text-balance leading-[1.05]">
            CMI. Dr. Biriș Cornelia
          </h1>
          <p className="mt-3 font-script text-brand-red text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Ro-Dental Cluj-Napoca
          </p>

          <p className="mt-5 max-w-2xl mx-auto text-brand-navy/75 text-base sm:text-lg font-light leading-relaxed">
            Stomatologie modernă în Cluj-Napoca — <strong className="text-brand-navy">{CLINIC.doctor}</strong>, medic primar stomatolog cu peste 25 de ani de experiență. Contract activ cu Casa de Asigurări de Sănătate Cluj.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3" data-testid="hero-ctas">
            <a
              href={CLINIC.phoneTel}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-red text-white font-semibold shadow-2xl hover:bg-brand-redSoft transition-colors"
              data-testid="hero-call-button"
            >
              <Phone className="w-5 h-5" />
              Sunați acum: {CLINIC.phonePretty}
            </a>
            <Link
              to="/programare"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-navy text-white font-semibold shadow-lg hover:bg-brand-navyDeep transition-colors"
              data-testid="hero-book-button"
            >
              <CalendarDays className="w-5 h-5" />
              Solicită o programare
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* PRIMA CONSULTAȚIE — punctul principal de intrare pentru pacienți noi */}
      <section className="bg-white border-y border-brand-beige">
        <div className="max-w-6xl mx-auto container-x py-12 sm:py-16">
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-brand-cream via-white to-brand-cream/50 border border-brand-yellow/40 shadow-soft">
            <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 sm:gap-10 p-6 sm:p-10 lg:p-12 items-center">
              <div>
                <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.24em] mb-3" data-testid="home-firstconsult-eyebrow">Pentru pacienți noi</p>
                <h2 className="font-display font-extrabold text-brand-navy text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15]">
                  Prima consultație – <span className="text-brand-red">primul pas către un plan de tratament construit împreună</span>
                </h2>
                <p className="mt-5 text-brand-navy/80 text-base sm:text-lg leading-relaxed font-light">
                  Prima consultație este etapa în care evaluăm starea de sănătate orală, stabilim diagnosticul și vă explicăm tratamentul necesar. Discutăm împreună opțiunile potrivite, etapele tratamentului și răspundem întrebărilor dumneavoastră, astfel încât decizia să fie luată în cunoștință de cauză.
                </p>
                <p className="mt-4 text-brand-navy/80 text-base sm:text-lg leading-relaxed font-light">
                  <strong className="text-brand-navy">Consultația poate fi decontată prin Casa de Asigurări de Sănătate</strong>, în condițiile CAS.
                </p>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to="/prima-consultatie"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border-2 border-brand-navy/15 text-brand-navy font-semibold text-sm hover:bg-brand-cream transition-colors"
                    data-testid="home-firstconsult-learn"
                  >
                    Aflați mai multe
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/programare?service=Prima%20consultație"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-bold text-sm tracking-wide uppercase shadow-lg hover:bg-brand-redSoft transition-colors"
                    data-testid="home-firstconsult-book"
                  >
                    <CalendarDays className="w-4 h-4" />
                    Solicită prima consultație
                  </Link>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-1 gap-3 md:gap-4 text-center" data-testid="home-firstconsult-steps">
                {["Clarificăm", "Înțelegem", "Alegem", "Planificăm"].map((label) => (
                  <div key={label} className="py-3 px-2 rounded-2xl bg-white/70 border border-brand-beige">
                    <p className="font-display font-bold text-brand-navy text-sm sm:text-base">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAM COMPLET — general + CAS inclus */}
      <section className="bg-white border-y border-brand-yellow/20" data-testid="home-schedule-section">
        <div className="max-w-6xl mx-auto container-x py-14 sm:py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.24em] mb-3">Program cabinet</p>
            <h2 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl tracking-tight">Programul complet al cabinetului</h2>
            <p className="mt-4 text-brand-navy/70 text-sm sm:text-base font-light leading-relaxed">
              Programul cu <strong className="text-brand-navy">Casa de Asigurări de Sănătate</strong> se află <strong>inclus în programul general</strong> al cabinetului.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
            {/* Program general */}
            <div className="rounded-3xl bg-brand-cream/60 border border-brand-yellow/50 p-5 sm:p-6 shadow-soft" data-testid="schedule-general">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-brand-yellow/70 text-brand-navy flex items-center justify-center">
                  <CalendarDays className="w-4 h-4" />
                </div>
                <h3 className="font-display font-bold text-brand-navy text-lg">Program general</h3>
              </div>
              <ul className="divide-y divide-brand-yellow/30 text-sm">
                {[
                  ["Luni", "08:00 – 14:00"],
                  ["Marți", "08:00 – 14:00"],
                  ["Miercuri", "14:00 – 19:00"],
                  ["Joi", "08:00 – 14:00"],
                  ["Vineri", "08:00 – 14:00"],
                ].map(([day, hours]) => (
                  <li key={day} className="flex items-center justify-between py-2.5">
                    <span className="font-semibold text-brand-navy">{day}</span>
                    <span className="text-brand-navy/80 tabular-nums">{hours}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Program CAS — inclus în programul general */}
            <div className="rounded-3xl bg-brand-navy text-white p-5 sm:p-6 shadow-soft relative overflow-hidden" data-testid="schedule-cas">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-brand-yellow text-brand-navy flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <h3 className="font-display font-bold text-white text-lg">Program cu Casa de Asigurări de Sănătate</h3>
              </div>
              <ul className="divide-y divide-white/15 text-sm">
                {[
                  ["Luni", "10:00 – 13:00"],
                  ["Marți", "09:00 – 12:00"],
                  ["Miercuri", "14:00 – 17:00"],
                  ["Joi", "10:00 – 13:00"],
                  ["Vineri", "10:00 – 13:00"],
                ].map(([day, hours]) => (
                  <li key={day} className="flex items-center justify-between py-2.5">
                    <span className="font-semibold text-white">{day}</span>
                    <span className="text-white/90 tabular-nums">{hours}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 pt-3 border-t border-white/15 text-xs text-brand-yellow/95 italic">
                Programul CAS se desfășoară în intervalul programului general.
              </p>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-sm">
            <a href={CLINIC.phoneTel} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-redSoft transition-colors" data-testid="schedule-call">
              <Phone className="w-4 h-4" /> {CLINIC.phonePretty}
            </a>
            <Link to="/programare" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white font-semibold hover:bg-brand-navyDeep transition-colors" data-testid="schedule-book">
              <CalendarDays className="w-4 h-4" /> Solicită o programare
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BAND — compact */}
      <section className="bg-brand-creamSoft border-b border-brand-yellow/20">
        <div className="max-w-7xl mx-auto container-x py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3" data-testid="casa-asigurari-badge">
            <div className="w-9 h-9 rounded-full bg-brand-green/15 flex items-center justify-center text-brand-green">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <div className="font-semibold text-brand-navy text-sm">Casa de Asigurări de Sănătate Cluj</div>
              <div className="text-xs text-brand-navy/60">Relație contractuală activă</div>
            </div>
          </div>
          <a href={CLINIC.phoneTel} className="text-sm font-semibold text-brand-red hover:underline">
            {CLINIC.phonePretty}
          </a>
        </div>
      </section>

      {/* SERVICES */}
      <section id="servicii" className="bg-brand-creamSoft">
        <div className="max-w-7xl mx-auto container-x py-24">
          <div className="max-w-3xl mb-14">
            <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">Servicii</p>
            <h2 className="font-display font-extrabold text-brand-navy text-4xl sm:text-5xl tracking-tight text-balance">
              Servicii stomatologice pentru copii și adulți
            </h2>
            <div className="mt-5 space-y-4 text-brand-navy/75 text-base sm:text-lg leading-relaxed font-light" data-testid="home-services-intro">
              <p>
                În cadrul <strong className="text-brand-navy">CMI Dr. Biriș Cornelia Maria</strong> sunt oferite consultații și tratamente de stomatologie generală pentru copii și adulți: profilaxie și igienizare profesională, tratamentul cariilor, tratamente de canal radicular, reabilitare orală cu lucrări protetice și estetică dentară.
              </p>
              <p>
                Consultațiile și tratamentele ortodontice sunt realizate <strong className="text-brand-navy">separat</strong>, în colaborare cu medicul ortodont.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="services-grid">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/programare?serviciu=${encodeURIComponent(s.title)}`} className="group" data-testid={`service-card-${s.slug}`}>
                <div className="h-full bg-white rounded-2xl overflow-hidden border border-brand-beige hover:border-brand-yellow hover:shadow-soft hover:-translate-y-0.5 transition-all duration-300">
                  <ServiceImage service={s} aspect="aspect-[16/10]" />
                  <div className="p-6">
                    <h3 className="font-display font-bold text-brand-navy text-lg leading-tight">{s.title}</h3>
                    <p className="mt-2 text-sm text-brand-navy/65 leading-relaxed">{s.short}</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-brand-red text-xs font-bold uppercase tracking-widest group-hover:gap-2.5 transition-all">
                      Solicită programare <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* DOCTOR */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto container-x py-24 grid lg:grid-cols-2 gap-14 items-center">
          <div className="relative no-img-drag order-2 lg:order-1">
            <img
              src={ASSETS.poster}
              alt="Dr. Cornelia Maria Biriș — Medic Primar Stomatolog"
              className="w-full max-w-md mx-auto rounded-2xl shadow-2xl"
              draggable={false}
              data-testid="doctor-poster"
            />
            <div className="absolute -bottom-4 -right-4 bg-brand-navy text-white px-5 py-3 rounded-2xl shadow-lg hidden sm:block">
              <Star className="w-4 h-4 mb-1" fill="white"/>
              <div className="font-bold text-sm">25+ ani</div>
              <div className="text-xs opacity-80">de experiență</div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">Despre medic</p>
            <h2 className="font-display font-extrabold text-brand-navy text-4xl sm:text-5xl tracking-tight">
              Dr. Cornelia Maria Biriș
            </h2>
            <p className="mt-2 text-brand-navy/65 font-medium">Medic Primar Stomatolog · Specialist în Stomatologie generală</p>
            <blockquote className="mt-7 pl-5 border-l-2 border-brand-pink text-brand-navy italic font-light">
              „{CLINIC.motto}"
            </blockquote>
            <Link
              to="/despre"
              className="mt-9 inline-flex items-center gap-2 text-brand-navy font-semibold hover:gap-3 transition-all border-b-2 border-brand-yellow pb-1"
              data-testid="home-about-button"
            >
              Aflați mai multe <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* INSIDE THE CABINET — single image, clean and uncluttered */}
      <section className="bg-gradient-to-b from-[#F8E8E0] via-[#FBEDD7] to-brand-creamSoft border-y border-brand-beige">
        <div className="max-w-7xl mx-auto container-x py-24">
          <div className="text-center mb-14">
            <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">CMI. Dr. Biriș Cornelia · Ro-Dental Cluj-Napoca</p>
            <h2 className="font-display font-extrabold text-brand-navy text-4xl sm:text-5xl tracking-tight">Pășiți în cabinet</h2>
          </div>

          <div className="max-w-4xl mx-auto no-img-drag">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white" data-testid="cabinet-composition-wrapper">
              <img
                src={ASSETS.cabinetComposition}
                alt="CMI. Dr. Biriș Cornelia — Ro-Dental Cluj-Napoca, interior cabinet"
                className="w-full h-auto block"
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                data-testid="cabinet-composition-image"
              />
            </div>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
              <Link to="/despre" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-navy text-white font-semibold hover:bg-brand-navyDeep transition-colors">
                Despre cabinet <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/programare" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-redSoft transition-colors">
                <CalendarDays className="w-4 h-4" /> Solicită o programare
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ARTICLES TEASER */}
      <section className="bg-brand-cream">
        <div className="max-w-7xl mx-auto container-x py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <div>
              <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">Educație orală</p>
              <h2 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl tracking-tight">Educație pentru sănătate orală</h2>
              <p className="mt-3 text-brand-navy/70 text-sm sm:text-base font-light">Articole și informații pentru pacienți.</p>
            </div>
            <Link to="/articole" className="inline-flex items-center gap-2 text-brand-navy font-semibold hover:gap-3 transition-all" data-testid="home-articles-link">
              <BookOpen className="w-4 h-4" /> Toate articolele <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS CTA */}
      <section className="bg-white border-y border-brand-yellow/20">
        <div className="max-w-7xl mx-auto container-x py-14">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex">
                  {[0,1,2,3,4].map((i) => (
                    <Star key={i} className="w-6 h-6 text-brand-yellow" fill="currentColor" />
                  ))}
                </div>
                <span className="text-brand-navy/60 text-sm font-medium">Recenzii Google</span>
              </div>
              <h2 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl tracking-tight">
                Ne-a făcut plăcere să vă fim de ajutor?
              </h2>
              <p className="mt-4 text-brand-navy/75 text-lg font-light leading-relaxed max-w-xl">
                O recenzie pe Google ne ajută să continuăm să oferim îngrijire stomatologică de calitate și ajută alți pacienți să ne descopere. Vă mulțumim.
              </p>
            </div>
            <div className="md:col-span-5 flex flex-col sm:flex-row md:flex-col gap-3">
              <a
                href={CLINIC.googleReviewUrl}
                target="_blank"
                rel="noreferrer"
                className="group inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-brand-navy text-white font-semibold shadow-lg hover:bg-brand-navyDeep transition-all hover:scale-[1.02]"
                data-testid="google-review-cta"
              >
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white text-[10px] font-bold tracking-tight">
                  <span className="text-[#4285F4]">G</span>
                </span>
                Scrie o recenzie pe Google
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href={CLINIC.googleMapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-white border border-brand-navy/15 text-brand-navy font-semibold hover:bg-brand-cream transition-colors text-sm"
                data-testid="google-maps-cta"
              >
                <MapPin className="w-4 h-4" /> Vezi pe Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy text-white relative overflow-hidden">
        <div
          aria-hidden
          className="absolute -right-20 -top-20 w-96 h-96 rounded-full opacity-[0.07]"
          style={{ backgroundImage: `url(${ASSETS.emblem})`, backgroundSize: 'cover' }}
        />
        <div className="relative max-w-7xl mx-auto container-x py-20 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-brand-yellow font-semibold text-xs uppercase tracking-[0.2em] mb-3">Programare</p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Doriți o programare la cabinet?</h2>
            <p className="mt-4 text-white/75 text-lg font-light">Ne puteți contacta telefonic sau puteți completa formularul online — vă vom contacta pentru confirmarea intervalului orar.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <a href={CLINIC.phoneTel} className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-brand-red text-white font-semibold hover:bg-brand-redSoft" data-testid="cta-call">
              <Phone className="w-5 h-5"/> {CLINIC.phonePretty}
            </a>
            <Link to="/programare" className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-white text-brand-navy font-semibold hover:bg-brand-cream" data-testid="cta-book">
              <CalendarDays className="w-5 h-5"/> Solicită o programare
            </Link>
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section className="bg-brand-creamSoft">
        <div className="max-w-7xl mx-auto container-x py-20 grid md:grid-cols-2 gap-10 items-start">
          <div>
            <p className="text-brand-red font-semibold text-xs uppercase tracking-[0.2em] mb-3">Locație</p>
            <h2 className="font-display font-extrabold text-brand-navy text-3xl sm:text-4xl tracking-tight">În Cluj-Napoca, pe Str. General Eremia Grigorescu</h2>
            <p className="mt-5 text-brand-navy/75 leading-relaxed font-light">Cabinetul stomatologic Ro-Dental se află pe strada General Eremia Grigorescu nr. 81, Cluj-Napoca. Acces facil cu mijloacele de transport în comun, cu autoturismul sau pietonal.</p>
            <p className="mt-3 text-brand-navy/65 text-sm leading-relaxed font-light" data-testid="home-legal-status">
              CMI Dr. Biriș Cornelia Maria își desfășoară activitatea independent în Cabinetul nr. 3, într-un spațiu medical închiriat.
            </p>
            <div className="mt-6 space-y-2.5 text-brand-navy">
              <div className="flex items-start gap-3 text-sm"><MapPin className="w-4 h-4 mt-0.5 text-brand-red"/>{CLINIC.address}</div>
              <div className="flex items-start gap-3 text-sm"><Phone className="w-4 h-4 mt-0.5 text-brand-red"/>
                <a href={CLINIC.phoneTel} className="font-semibold hover:underline">{CLINIC.phonePretty}</a>
              </div>
            </div>
            <Link to="/contact" className="mt-7 inline-flex items-center gap-2 text-brand-navy font-semibold hover:gap-3 border-b-2 border-brand-yellow pb-1">
              Vezi detalii și hartă <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-soft border border-brand-beige h-80">
            <MapEmbed
              title="Ro-Dental Cluj-Napoca — locație"
              src={CLINIC.mapsEmbed}
              mapsUrl={CLINIC.googleMapsUrl}
              className="w-full h-80"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
